import React, { Suspense, useMemo } from 'react';
import './OverviewSection.scss';
import { Stack, Typography, Tooltip, CircularProgress } from '@mui/material';
import { useCircadianAnalysis } from '../../../../data-access';
import { ReactComponent as FlameIcon } from '../../../../assets/icons/flame-icon.svg';
import { Bedtime, NorthEast, SouthEast, East } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { selectedDateAtom, dataTrendPeriodAtom } from '../../../../data-access';
import dayjs from 'dayjs';
import { useCalendarTrend, useEnergyTrend } from '../../../../hooks';
import { useSleepTrend } from '../../../../hooks/useSleepTrend';
import { useTaskTrend } from '../../../../hooks/useTaskTrend';
import { getCssVariable } from '../../../../utils';
import {
  getSatisfactionIcon,
  satisfactionColor,
} from '../../../DataView/constants';
import { useAuthContext, useSatisfactionEmas } from '../../../../data-access';
import { PeakDipsGraph } from '../../DataCircadianDashboard';
import { EnergyDataTrend } from '@demind-inc/core';
import { ReactComponent as PieIcon } from '../../../../assets/icons/pie-icon.svg';
import { ReactComponent as BrainIcon } from '../../../../assets/icons/brain-icon.svg';
import { DataSatisfaction } from '../DataSatisfaction';
import { TextLoader } from '../../../common';
import { useTranslation } from 'react-i18next';
import {
  formatDurationHoursMinutes,
  getSatisfactionText as getTranslatedSatisfactionText,
} from '../../i18n';

const LazyCircadianChart = React.lazy(
  () => import('../../DataCircadianDashboard/CircadianChart/CircadianChart')
);

interface OverviewCardItem {
  icon: React.ReactNode;
  value: string | number;
  diff?: number | string;
  diffLabel?: string;
  isIncrease?: boolean;
  diffValue?: number;
}

const blackPrimary = getCssVariable('--color-black-primary');

const OverviewSection: React.FC = () => {
  const { t } = useTranslation();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);

  const {
    peakEnergyDiff,
    energyDailyTrend,
    energyWeeklyTrend,
    isLoading: energyLoading,
  } = useEnergyTrend();
  const { targetSleepHours, isLoading: sleepLoading, hasNoSleepData } = useSleepTrend();
  const {
    focusTime,
    focusTimeDiff,
    isLoading: calendarLoading,
  } = useCalendarTrend({
    date: selectedDate,
  });
  const { selectedDateCircadianChartData, selectedDateEnergyFluctuations, avgCircadianChartData } =
    useCircadianAnalysis();
  const { commonData, isLoading: taskLoading } = useTaskTrend({ date: selectedDate });
  const { user } = useAuthContext();
  const isWeekly = dataTrendPeriod === 'weekly';
  const chartData = useMemo(() => {
    return ((energyWeeklyTrend?.data ?? []) as EnergyDataTrend[]).map(
      (item) => item.peakEnergy ?? 0
    );
  }, [energyWeeklyTrend]);
  const weekRange = useMemo(
    () => ({
      startDate: dayjs(selectedDate).startOf('week').format('YYYY-MM-DD'),
      endDate: dayjs(selectedDate).endOf('week').format('YYYY-MM-DD'),
    }),
    [selectedDate]
  );

  const { satisfactionEmas, isLoading: satisfactionLoading } = useSatisfactionEmas({
    userId: user?.userId,
    ...weekRange,
  });
  const taskCompletion = useMemo(() => {
    const completionPercentRaw = commonData.taskCompletion?.current;
    const completionDiffRaw = commonData.taskCompletion?.diff;

    const numValue =
      typeof completionPercentRaw === 'number'
        ? completionPercentRaw
        : Number(completionPercentRaw) || 0;

    const completionPercent = numValue < 0 ? '--' : numValue;
    const completionDiff =
      typeof completionDiffRaw === 'number' ? completionDiffRaw : Number(completionDiffRaw) || 0;
    return { completionPercent, completionDiff };
  }, [commonData.taskCompletion]);

  const satisfactionData = useMemo(() => {
    const currentDateFormatted = dayjs(selectedDate).format('YYYY-MM-DD');
    const currentSatisfaction =
      satisfactionEmas.find((d) => d.date === currentDateFormatted)?.satisfaction?.value ||
      'unanswered';

    return {
      icon: getSatisfactionIcon(currentSatisfaction, 'selected', 20),
      text: getTranslatedSatisfactionText(t, currentSatisfaction),
      color: satisfactionColor[currentSatisfaction],
    };
  }, [selectedDate, satisfactionEmas, t]);

  const overviewData: OverviewCardItem[] = useMemo(
    () => [
      {
        icon: <FlameIcon className="overview-section__icon" fill={blackPrimary} />,
        value: Math.round(
          isWeekly ? (energyWeeklyTrend?.avgPeakEnergy ?? 0) : (energyDailyTrend?.peakEnergy ?? 0)
        ),
        diff: peakEnergyDiff,
      },
      {
        icon: <Bedtime className="overview-section__icon" />,
        value: hasNoSleepData
          ? t('data.common.emptyDuration')
          : formatDurationHoursMinutes(t, targetSleepHours.hours, targetSleepHours.minutes),
        diff: undefined,
      },
      {
        icon: <BrainIcon className="overview-section__icon" />,
        value: focusTime,
        diff: focusTimeDiff.formatted,
        diffLabel: '',
        isIncrease: focusTimeDiff.value > 0,
        diffValue: focusTimeDiff.value,
      },
      {
        icon: <PieIcon className="overview-section__icon" />,
        value:
          taskCompletion.completionPercent === '--'
            ? '--'
            : `${Math.round(Number(taskCompletion.completionPercent))}%`,
        diff: taskCompletion.completionDiff,
        diffLabel: '',
        isIncrease: taskCompletion.completionDiff > 0,
      },
    ],
    [
      energyDailyTrend?.peakEnergy,
      energyWeeklyTrend?.avgPeakEnergy,

      peakEnergyDiff,
      targetSleepHours,
      hasNoSleepData,
      focusTime,
      focusTimeDiff,
      taskCompletion,
      t,
    ]
  );

  const colorGreen = getCssVariable('--color-green-base');
  const colorRed = getCssVariable('--color-red-base');
  const colorGray = getCssVariable('--color-gray-dark');

  const renderDiffIcon = (diffNum: number) => {
    if (diffNum > 0) return <NorthEast className="overview-section__diff-icon" />;
    if (diffNum < 0) return <SouthEast className="overview-section__diff-icon" />;
    if (diffNum === 0) return '-';
    return null;
  };

  const getDiffColor = (diffNum: number) => {
    if (diffNum > 0) return colorGreen;
    if (diffNum < 0) return colorRed;
    if (diffNum === 0) return colorGray;
    return 'inherit';
  };

  const shouldShowDiff = (idx: number, diff: number | string | undefined) => {
    return (idx === 0 || idx === 2 || idx === 3) && diff !== undefined;
  };

  const getTooltipContent = (idx: number, value: string | number) => {
    const isWeekly = dataTrendPeriod === 'weekly';

    switch (idx) {
      case 0: // Peak Energy
        return t('data.metrics.tooltipWithValue', {
          label: isWeekly
            ? t('data.metrics.averagePeakEnergy')
            : t('data.metrics.peakEnergy'),
          value,
        });
      case 1: // Sleep Duration
        return t('data.metrics.tooltipWithValue', {
          label: isWeekly
            ? t('data.metrics.averageSleepDuration')
            : t('data.metrics.sleepDuration'),
          value,
        });
      case 2: // Focus Time
        return t('data.metrics.tooltipWithValue', {
          label: isWeekly ? t('data.metrics.totalFocusTime') : t('data.metrics.focusTime'),
          value,
        });
      case 3: // Task Completion
        return t('data.metrics.tooltipWithValue', {
          label: isWeekly
            ? t('data.metrics.averageTaskCompletion')
            : t('data.metrics.taskCompletion'),
          value,
        });
      case 4: // Satisfaction
        return t('data.metrics.tooltipWithValue', {
          label: t('data.satisfaction.title'),
          value,
        });
      default:
        return '';
    }
  };

  const renderValue = (value: string | number, index: number) => {
    if (index === 0) {
      return energyLoading ? <TextLoader /> : value;
    }
    if (index === 1) {
      return sleepLoading ? <TextLoader /> : value;
    }
    if (index === 2) {
      return calendarLoading ? <TextLoader /> : value;
    }
    if (index === 3) {
      return taskLoading ? <TextLoader /> : value;
    }
    return value;
  };
  return (
    <div className="overview-section">
      <Typography variant="body1" className="overview-section__title">
        {t('data.common.overview')}
      </Typography>
      <Stack gap={1.5} flex={1} mt={2} className="overview-section__body">
        {isWeekly ? (
          <PeakDipsGraph
            className="overview-section__chart overview-section__chart--weekly"
            chartData={chartData.filter((item) => item)}
            height={286}
          />
        ) : (
          <Suspense>
            <LazyCircadianChart
              selectedDateCircadianData={selectedDateCircadianChartData}
              selectedDateEnergyFluctuations={selectedDateEnergyFluctuations}
              avgCircadianData={avgCircadianChartData}
              hideLegend
              isSimplified
              className="overview-section__chart"
            />
          </Suspense>
        )}
        <Stack
          direction="row"
          gap={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="stretch"
          className="overview-section__cards"
        >
          {overviewData.map((item, idx) => {
            const diffNum =
              typeof item.diff === 'number'
                ? item.diff
                : idx === 2
                  ? item.diffValue
                  : parseFloat(item.diff || '0');

            return (
              <Tooltip key={idx} title={getTooltipContent(idx, item.value)} placement="right" arrow>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  className="overview-section__card"
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    {item.icon}
                    <Typography
                      variant="body1"
                      className="overview-section__title"
                      component="span"
                    >
                      {renderValue(item.value, idx)}
                    </Typography>
                  </Stack>

                  {shouldShowDiff(idx, item.diff) && (
                    <Typography
                      variant="body2"
                      color={getDiffColor(diffNum)}
                      className="overview-section__title"
                    >
                      {renderDiffIcon(diffNum)}
                      {idx === 2 ? item.diff : Math.abs(Math.round(diffNum))}
                      {idx === 3 ? '%' : ''}
                    </Typography>
                  )}

                  {item.diff !== undefined && item.diffLabel && idx !== 3 && (
                    <Typography variant="body2" color={getDiffColor(diffNum)}>
                      {diffNum > 0 ? '↑' : diffNum < 0 ? '↓' : ''} {item.diff}
                    </Typography>
                  )}
                </Stack>
              </Tooltip>
            );
          })}

          {satisfactionLoading ? (
            <Stack
              justifyContent="center"
              className="overview-section__card"
              direction="row"
              alignItems="center"
              gap={1}
            >
              <CircularProgress size={20} />
            </Stack>
          ) : isWeekly ? (
            <Stack className="overview-section__card overview-section__card--satisfaction">
              <DataSatisfaction isSimplified />
            </Stack>
          ) : (
            <Tooltip title={getTooltipContent(4, satisfactionData.text)} placement="right" arrow>
              <Stack className="overview-section__card" direction="row" alignItems="center" gap={1}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {satisfactionData.icon}
                </span>
                <Typography
                  variant="body1"
                  className="overview-section__title"
                  color={satisfactionData.color}
                >
                  {satisfactionData.text}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

export default OverviewSection;
